import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import type { BillingInfo, BillingInfoPrivate, BillingInfoCompany } from "@/components/booking/InvoiceBillingForm";
import { validateBillingInfo } from "@/components/booking/InvoiceBillingForm";

export default function ProfileBillingSection() {
  const { toast } = useToast();
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingType, setBillingType] = useState<"private" | "company">("private");
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) return;
        const { data: prof, error } = await (supabase as any)
          .from("profiles")
          .select("id, billing_info")
          .eq("user_id", user.id)
          .maybeSingle();
        if (error) throw error;
        if (prof?.id) setProfileId(prof.id);
        const saved = (prof?.billing_info as BillingInfo) || null;
        if (saved?.type === "private" || saved?.type === "company") {
          setBillingType(saved.type as "private" | "company");
          setBilling(saved);
        } else {
          setBillingType("private");
          setBilling({ type: "private", first_name: "", last_name: "", address: "", codice_fiscale: "" });
        }
      } catch (_) {
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    const current: BillingInfo =
      billingType === "private"
        ? (billing as BillingInfoPrivate) || { type: "private", first_name: "", last_name: "", address: "", codice_fiscale: "" }
        : (billing as BillingInfoCompany) || { type: "company", business_name_or_full_name: "", address: "", codice_univoco_sdi: "" };
    const err = validateBillingInfo(current);
    if (err) {
      toast({ title: t("profile.billing.invalid"), description: err, variant: "destructive" });
      return;
    }
    if (!profileId) {
      toast({ title: t("profile.toast.error"), description: t("profile.toast.profile_missing_desc"), variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from("profiles")
        .update({ billing_info: current, updated_at: new Date().toISOString() })
        .eq("id", profileId);
      if (error) throw error;
      setBilling(current);
      toast({ title: t("profile.billing.saved"), description: t("profile.billing.saved_desc") });
    } catch (e: any) {
      toast({ title: t("profile.toast.error"), description: e?.message || t("profile.billing.save_failed"), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const updatePrivate = (field: keyof BillingInfoPrivate, value: string) => {
    const prev = (billing as BillingInfoPrivate) || { type: "private", first_name: "", last_name: "", address: "", codice_fiscale: "" };
    setBilling({ ...prev, [field]: value });
  };
  const updateCompany = (field: keyof BillingInfoCompany, value: string) => {
    const prev = (billing as BillingInfoCompany) || { type: "company", business_name_or_full_name: "", address: "", codice_univoco_sdi: "" };
    setBilling({ ...prev, [field]: value });
  };

  if (loading) return <div className="text-sm text-muted-foreground">{t("profile.loading")}</div>;

  return (
    <Card key={lang} className="border-gray-200 shadow-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-900">{t("profile.billing.title")}</CardTitle>
        <p className="text-sm text-muted-foreground">{t("profile.billing.subtitle")}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm font-medium">{t("profile.billing.type")}</Label>
          <RadioGroup
            value={billingType}
            onValueChange={(v) => {
              const nextType = v as "private" | "company";
              setBillingType(nextType);
              if (nextType === "company") {
                const prev = billing as BillingInfoCompany | undefined;
                setBilling({ type: "company", business_name_or_full_name: prev?.business_name_or_full_name ?? "", address: prev?.address ?? "", codice_univoco_sdi: prev?.codice_univoco_sdi ?? "" });
              } else {
                const prev = billing as BillingInfoPrivate | undefined;
                setBilling({ type: "private", first_name: prev?.first_name ?? "", last_name: prev?.last_name ?? "", address: prev?.address ?? "", codice_fiscale: prev?.codice_fiscale ?? "" });
              }
            }}
            className="mt-2 flex gap-4"
          >
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value="private" />
              <span className="text-sm">{t("profile.billing.type_private")}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioGroupItem value="company" />
              <span className="text-sm">{t("profile.billing.type_company")}</span>
            </label>
          </RadioGroup>
        </div>

        {billingType === "private" && (
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">{t("profile.billing.first_name")}</Label>
                <Input
                  value={(billing as BillingInfoPrivate)?.first_name ?? ""}
                  onChange={(e) => updatePrivate("first_name", e.target.value)}
                  placeholder={t("profile.billing.placeholder_first")}
                />
              </div>
              <div>
                <Label className="text-sm">{t("profile.billing.last_name")}</Label>
                <Input
                  value={(billing as BillingInfoPrivate)?.last_name ?? ""}
                  onChange={(e) => updatePrivate("last_name", e.target.value)}
                  placeholder={t("profile.billing.placeholder_last")}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">{t("profile.billing.address")}</Label>
              <Input
                value={(billing as BillingInfoPrivate)?.address ?? ""}
                onChange={(e) => updatePrivate("address", e.target.value)}
                placeholder={t("profile.billing.placeholder_address")}
              />
            </div>
            <div>
              <Label className="text-sm">{t("profile.billing.tax_code")}</Label>
              <Input
                className="font-mono uppercase"
                value={(billing as BillingInfoPrivate)?.codice_fiscale ?? ""}
                onChange={(e) => updatePrivate("codice_fiscale", e.target.value.toUpperCase())}
                placeholder={t("profile.billing.placeholder_tax")}
                maxLength={16}
              />
            </div>
          </div>
        )}

        {billingType === "company" && (
          <div className="grid gap-3">
            <div>
              <Label className="text-sm">{t("profile.billing.business_name")}</Label>
              <Input
                value={(billing as BillingInfoCompany)?.business_name_or_full_name ?? ""}
                onChange={(e) => updateCompany("business_name_or_full_name", e.target.value)}
                placeholder={t("profile.billing.placeholder_company")}
              />
            </div>
            <div>
              <Label className="text-sm">{t("profile.billing.company_address")}</Label>
              <Input
                value={(billing as BillingInfoCompany)?.address ?? ""}
                onChange={(e) => updateCompany("address", e.target.value)}
                placeholder={t("profile.billing.placeholder_address")}
              />
            </div>
            <div>
              <Label className="text-sm">{t("profile.billing.sdi_code")}</Label>
              <Input
                className="font-mono uppercase"
                value={(billing as BillingInfoCompany)?.codice_univoco_sdi ?? ""}
                onChange={(e) => updateCompany("codice_univoco_sdi", e.target.value.toUpperCase().slice(0, 7))}
                placeholder={t("profile.billing.placeholder_sdi")}
                maxLength={7}
              />
            </div>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving}>
          {saving ? t("profile.billing.saving") : t("profile.billing.save")}
        </Button>
      </CardContent>
    </Card>
  );
}
